import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { offlineAwareBaseQuery } from './offline-api-util';

/**
 * True when WebView mode is enabled via the REACT_APP_WEBVIEW_MODE_ENABLED env var.
 * When enabled, API requests from the web app running in a React Native WebView
 * will be sent to the native app via postMessage instead of using fetch directly.
 */
const isWebViewModeEnabled = process.env.REACT_APP_WEBVIEW_MODE_ENABLED === 'true';

interface ApiRequestMessage {
  type: 'API_REQUEST';
  requestId: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  headers?: Record<string, unknown>;
  body?: string | null;
}

interface ApiMutationMessage {
  type: 'API_MUTATION';
  requestId: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  headers?: Record<string, unknown>;
  body?: string | null;
}

interface ApiResponseMessage {
  type: 'API_RESPONSE';
  requestId: string;
  status: number;
  statusText: string;
  headers?: Record<string, unknown>;
  body: string | null;
}

/**
 * Checks if the code is running inside a React Native WebView.
 * @returns true if window.ReactNativeWebView exists, false otherwise.
 */
export const isWebView = (): boolean => {
  return typeof window !== 'undefined' && 'ReactNativeWebView' in window;
};

/**
 * Sends an API request or mutation message to React Native via postMessage
 * and waits for the response.
 *
 * @param message The API request or mutation message to send
 * @param timeoutMs Timeout in milliseconds (default: 45000)
 * @returns Promise that resolves with the API response
 */
const sendMessageToNative = (
  message: ApiRequestMessage | ApiMutationMessage,
  timeoutMs = 45000
): Promise<ApiResponseMessage> => {
  return new Promise((resolve, reject) => {
    const requestId = message.requestId;

    const handleMessage = (event: MessageEvent) => {
      try {
        const response = JSON.parse(event.data) as ApiResponseMessage;

        if (response.type === 'API_RESPONSE' && response.requestId === requestId) {
          clearTimeout(timeoutId);
          cleanup();
          resolve(response);
        }
      } catch {
        // Ignore parse errors - message might not be for us
      }
    };

    // react-native-webview dispatches MessageEvent on different targets per platform:
    //   iOS (WKWebView):  window.dispatchEvent(new MessageEvent('message', ...))
    //   Android (WebView): document.dispatchEvent(new MessageEvent('message', ...))
    // Listen on both to ensure cross-platform compatibility.
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as EventListener);
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`WebView API request timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as EventListener);

    window.ReactNativeWebView?.postMessage(JSON.stringify(message));
  });
};

/**
 * Base query wrapper that routes API requests through React Native WebView
 * when running in WebView context with the feature flag enabled.
 *
 * When REACT_APP_WEBVIEW_MODE_ENABLED === 'true' and window.ReactNativeWebView exists,
 * it sends API requests to the native app via postMessage instead of using fetch.
 *
 * Otherwise, it falls back to the normal baseQueryWithReAuthAndUrlEncoding.
 *
 * @param args Original RTK Query fetch args (string URL or FetchArgs).
 * @param api RTK Query API utilities.
 * @param extraOptions Additional options passed to the underlying base query.
 *
 * @returns The API response from either the native app or the normal base query.
 */
export const webViewBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  if (!isWebView() || !isWebViewModeEnabled) {
    return offlineAwareBaseQuery(args, api, extraOptions);
  }

  try {
    let url = typeof args === 'string' ? args : args.url;
    const method = typeof args === 'string' ? 'GET' : args.method?.toUpperCase() || 'GET';
    const body = typeof args === 'string' ? null : JSON.stringify(args.body);

    if (typeof args !== 'string' && args.params) {
      const filteredParams: Record<string, string> = {};
      Object.entries(args.params).forEach(([key, value]) => {
        if (value !== undefined) {
          filteredParams[key] = value;
        }
      });

      const queryString = new URLSearchParams(filteredParams).toString();
      if (queryString) {
        const divider = url.indexOf('?') !== -1 ? '&' : '?';
        url = `${url}${divider}${queryString}`;
      }
    }

    const headers: Record<string, unknown> = {};
    const clusterConfig = JSON.parse(localStorage.getItem('ClusterConfig') || '{}');
    const userState = JSON.parse(localStorage.getItem('UserState') || '{}');

    if (clusterConfig.apiKey) {
      headers['x-api-key'] = clusterConfig.apiKey;
    }
    if (userState?.tokens?.id_token) {
      headers['authorization'] = `Bearer ${userState.tokens.id_token}`;
    }
    if (userState?.user?.currentSchema) {
      headers['Myosh-Schema'] = userState.user.currentSchema;
    }
    if (body) {
      headers['Content-Type'] = 'application/json';
    }

    const apiBaseUrl = clusterConfig.apiBaseUrl || '';
    const baseUrl = `${apiBaseUrl}v4`;
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

    const requestId = crypto.randomUUID();
    const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

    const message: ApiRequestMessage | ApiMutationMessage = isMutation
      ? {
          type: 'API_MUTATION',
          requestId,
          method: method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
          url: fullUrl,
          headers,
          body,
        }
      : {
          type: 'API_REQUEST',
          requestId,
          method: method as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
          url: fullUrl,
          headers,
          body,
        };

    const response = await sendMessageToNative(message);

    // Convert response to RTK Query format
    if (response.status >= 200 && response.status < 300) {
      const responseData = response.body ? JSON.parse(response.body) : null;

      // The "x-queued" header indicates a mutation was queued successfully by the native
      // app while the device was offline. We detect this to conditionally prevent
      // RTK Query cache invalidation, avoiding immediate refetches that would serve stale data.
      const isQueued = response.headers && response.headers['x-queued'] === 'true';
      if (responseData && responseData._queued === true) {
        return {
          data: responseData,
        };
      }

      // If x-queued header is present, pass it through meta so invalidatesTags can detect it
      if (isQueued) {
        return {
          data: responseData,
          meta: { queuedOffline: true },
        };
      }

      return {
        data: responseData,
      };
    } else {
      return {
        error: {
          status: response.status,
          data: response.body ? JSON.parse(response.body) : null,
        },
      };
    }
  } catch (error) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: error instanceof Error ? error.message : 'WebView API request failed',
      },
    };
  }
};
